import { useEffect, useMemo, useRef } from "react";

import RestaurantLanding from "@/template-restaurant/pages/Landing";
import { setRestaurantData } from "@/template-restaurant/lib/_data";
import "@/template-restaurant/restaurant.css";

/**
 * Shell that renders the Standard Restaurant Template for any published site
 * with `industry_template === "restaurant"`.
 *
 * Mirrors PublicServiceShell:
 *   1. Synchronously mutates the template's `_data` module with the
 *      backend-built `restaurant_config` inside `useMemo` so the very
 *      first render already sees real content.
 *   2. Wraps the Landing page in a class that scopes the template's
 *      custom CSS (.label, .link-underline, .fade-up, .font-serif,
 *      .font-mono) — see template-restaurant/restaurant.css.
 *   3. Passes the `slug` down so the embedded ReservationForm can POST to
 *      `/api/public/site/:slug/reservations`.
 */
export default function PublicRestaurantShell({ slug, restaurantConfig }) {
    const configRef = useRef(null);

    useMemo(() => {
        if (restaurantConfig && configRef.current !== restaurantConfig) {
            setRestaurantData(restaurantConfig);
            configRef.current = restaurantConfig;
        }
    }, [restaurantConfig]);

    // The restaurant template is a single-page scroll experience — reset
    // scroll to top whenever we mount a new business.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    return (
        <div className="restaurant-template" data-testid="public-restaurant-shell" data-published-site="1">
            <RestaurantLanding slug={slug} />
        </div>
    );
}
